// AUTO-SEEDED from the (now-retired) GET /milestones endpoint on 2026-07-26.
//
// Milestones are historic "firsts" — immutable facts. Recomputing them at
// request time meant 18 parallel threshold scans against `entries`, which
// started blowing the 20s statement timeout (500s on the live page). Since
// the answers never change, we hardcode them here instead: the page loads
// instantly, makes zero API calls, and never 500s.
//
// TO ADD A MILESTONE: append an entry below (keep the array sorted by
// meet.meet_date so the timeline reads chronologically). Values are in kg.
//
// IMAGES: the `image` block is supported (rendered on the scene with credit
// + license), but a milestone photo must be the lifter ACTUALLY making that
// lift, or a shot from that meet — not a generic headshot. None currently
// meet that bar with an open license; copyrighted candidates + rights-holder
// contacts are staged in /milestone-images-pending/ pending permission.
import type { Milestone } from '../api';

export interface MilestoneImage {
  /** Public path under /images/milestones/. Must be an action/meet shot. */
  src: string;
  /** Author to credit; '' when the license (CC0/PD) requires none. */
  credit: string;
  /** e.g. 'CC BY-SA 4.0' / 'CC0'. */
  license: string;
  licenseUrl: string;
  /** Origin (e.g. Wikimedia Commons file page). */
  sourceUrl: string;
}
export type MilestoneWithImage = Milestone & { image?: MilestoneImage };

export const MILESTONES: MilestoneWithImage[] = [
  {
    "id": "first-m-raw-squat-300",
    "title": "First man to squat 300 kg raw",
    "story": "The first time someone got 300 kilos out of the rack and back up without a squat suit — wraps allowed in the earlier era, sleeves in the modern one.",
    "icon": "⬇️",
    "threshold": 300,
    "threshold_field": "best3_squat_kg",
    "lifter": {
      "lifter_id": 121552,
      "name": "Terence Todd"
    },
    "meet": {
      "meet_id": 238,
      "meet_name": "Senior Nationals",
      "federation": "AAU",
      "meet_date": "1965-09-04"
    },
    "result": {
      "total": 858.2,
      "squat": 306.18,
      "bench": 216.36,
      "deadlift": 335.66,
      "dots": 460.19,
      "equipment": "Wraps",
      "sex": "M",
      "weight_class_kg": "90+",
      "bodyweight_kg": 151.61
    }
  },
  {
    "id": "first-m-raw-squat-350",
    "title": "First man to squat 350 kg raw",
    "story": "Halfway between the mythical 300 and the superhuman 400 — the first 350 raw squat marked the point where sleeves-only lifting caught the old suited record books.",
    "icon": "⬇️",
    "threshold": 350,
    "threshold_field": "best3_squat_kg",
    "lifter": {
      "lifter_id": 104782,
      "name": "Robert Weaver"
    },
    "meet": {
      "meet_id": 239,
      "meet_name": "Senior Nationals",
      "federation": "AAU",
      "meet_date": "1966-09-03"
    },
    "result": {
      "total": 874.98,
      "squat": 354.71,
      "bench": 225.44,
      "deadlift": 294.84,
      "dots": null,
      "equipment": "Wraps",
      "sex": "M",
      "weight_class_kg": "90+",
      "bodyweight_kg": null
    }
  },
  {
    "id": "first-m-raw-total-900",
    "title": "First man to total 900 kg raw",
    "story": "For decades this looked like a hard ceiling for un-suited lifters. Suited and multi-ply lifters cleared it routinely, but in a no-suit configuration (Raw or Wraps) it stayed out of reach until this meet.",
    "icon": "🦾",
    "threshold": 900,
    "threshold_field": "total_kg",
    "lifter": {
      "lifter_id": 967398,
      "name": "Don Cundy"
    },
    "meet": {
      "meet_id": 242,
      "meet_name": "Senior Nationals",
      "federation": "AAU",
      "meet_date": "1969-08-29"
    },
    "result": {
      "total": 918.53,
      "squat": 333.39,
      "bench": 222.26,
      "deadlift": 362.87,
      "dots": null,
      "equipment": "Wraps",
      "sex": "M",
      "weight_class_kg": "110+",
      "bodyweight_kg": null
    }
  },
  {
    "id": "first-m-raw-bench-250",
    "title": "First man to bench 250 kg raw",
    "story": "Bench is the slowest lift to scale. 250 in a no-shirt configuration stayed elite long after squats and deadlifts blew past their analogs.",
    "icon": "💪",
    "threshold": 250,
    "threshold_field": "best3_bench_kg",
    "lifter": {
      "lifter_id": 733243,
      "name": "Jim Williams #1"
    },
    "meet": {
      "meet_id": 242,
      "meet_name": "Senior Nationals",
      "federation": "AAU",
      "meet_date": "1969-08-29"
    },
    "result": {
      "total": 910.13,
      "squat": 331.12,
      "bench": 272.84,
      "deadlift": 306.18,
      "dots": 486.2,
      "equipment": "Wraps",
      "sex": "M",
      "weight_class_kg": "110+",
      "bodyweight_kg": 153.77
    }
  },
  {
    "id": "first-m-raw-total-1000",
    "title": "First man to total 1000 kg raw",
    "story": "The first time anyone added a squat, bench, and deadlift to a four-digit total without a suit or briefs. Made the impossible ordinary.",
    "icon": "👑",
    "threshold": 1000,
    "threshold_field": "total_kg",
    "lifter": {
      "lifter_id": 978691,
      "name": "Jon Cole #1"
    },
    "meet": {
      "meet_id": 247,
      "meet_name": "Senior Nationals",
      "federation": "AAU",
      "meet_date": "1972-09-16"
    },
    "result": {
      "total": 1024.67,
      "squat": 394.17,
      "bench": 258.55,
      "deadlift": 371.95,
      "dots": null,
      "equipment": "Wraps",
      "sex": "M",
      "weight_class_kg": "110+",
      "bodyweight_kg": null
    }
  },
  {
    "id": "first-m-raw-total-1050",
    "title": "First man to total 1050 kg raw",
    "story": "Just two months after Jon Cole made 1000 ordinary, John Kuc cleared the next fifty. A super-heavyweight from Pennsylvania, Kuc went on to a powerlifting Hall-of-Fame career. The 1000 club barely had time to feel exclusive.",
    "icon": "🏔️",
    "threshold": 1050,
    "threshold_field": "total_kg",
    "lifter": {
      "lifter_id": 19086,
      "name": "John Kuc"
    },
    "meet": {
      "meet_id": 248,
      "meet_name": "Men's World Powerlifting Championships",
      "federation": "AAU",
      "meet_date": "1972-11-10"
    },
    "result": {
      "total": 1065.94,
      "squat": 410.5,
      "bench": 272.16,
      "deadlift": 383.29,
      "dots": 577.36,
      "equipment": "Wraps",
      "sex": "M",
      "weight_class_kg": "110+",
      "bodyweight_kg": 146.06
    }
  },
  {
    "id": "first-m-raw-squat-400",
    "title": "First man to squat 400 kg raw",
    "story": "Four hundred kilos out of the rack and back up, no suit. The cleanest single-lift threshold of the modern raw era.",
    "icon": "⬇️",
    "threshold": 400,
    "threshold_field": "best3_squat_kg",
    "lifter": {
      "lifter_id": 19086,
      "name": "John Kuc"
    },
    "meet": {
      "meet_id": 248,
      "meet_name": "Men's World Powerlifting Championships",
      "federation": "AAU",
      "meet_date": "1972-11-10"
    },
    "result": {
      "total": 1065.94,
      "squat": 410.5,
      "bench": 272.16,
      "deadlift": 383.29,
      "dots": 577.36,
      "equipment": "Wraps",
      "sex": "M",
      "weight_class_kg": "110+",
      "bodyweight_kg": 146.06
    }
  },
  {
    "id": "first-m-raw-bench-300",
    "title": "First man to bench 300 kg raw",
    "story": "The slowest lift to scale finally cracked three hundred without a shirt — a number that lived only in bench-shirt record books for decades.",
    "icon": "💪",
    "threshold": 300,
    "threshold_field": "best3_bench_kg",
    "lifter": {
      "lifter_id": 733243,
      "name": "Jim Williams #1"
    },
    "meet": {
      "meet_id": 248,
      "meet_name": "Men's World Powerlifting Championships",
      "federation": "AAU",
      "meet_date": "1972-11-10"
    },
    "result": {
      "total": 1009.24,
      "squat": 374.21,
      "bench": 306.17,
      "deadlift": 328.85,
      "dots": 537.48,
      "equipment": "Wraps",
      "sex": "M",
      "weight_class_kg": "110+",
      "bodyweight_kg": 155.58
    }
  },
  {
    "id": "first-f-raw-total-400",
    "title": "First woman to total 400 kg raw",
    "story": "Marks the moment women's powerlifting started producing globally elite numbers in the no-suit configuration.",
    "icon": "🏋️‍♀️",
    "threshold": 400,
    "threshold_field": "total_kg",
    "lifter": {
      "lifter_id": 34573,
      "name": "Jan Todd"
    },
    "meet": {
      "meet_id": 256,
      "meet_name": "First All American Women's Power Meet",
      "federation": "AAU",
      "meet_date": "1977-04-17"
    },
    "result": {
      "total": 439.99,
      "squat": 174.63,
      "bench": 77.11,
      "deadlift": 188.24,
      "dots": null,
      "equipment": "Wraps",
      "sex": "F",
      "weight_class_kg": "79.3+",
      "bodyweight_kg": null
    }
  },
  {
    "id": "first-m-raw-deadlift-400",
    "title": "First man to deadlift 400 kg raw",
    "story": "The Hall-of-Fame number. 400 kg off the floor without a deadlift suit puts you in a tiny club of all-time pullers.",
    "icon": "⬆️",
    "threshold": 400,
    "threshold_field": "best3_deadlift_kg",
    "lifter": {
      "lifter_id": 500337,
      "name": "Mark Henry #1"
    },
    "meet": {
      "meet_id": 851,
      "meet_name": "Men's Nationals",
      "federation": "ADFPA",
      "meet_date": "1995-07-15"
    },
    "result": {
      "total": 1050,
      "squat": 430,
      "bench": 210,
      "deadlift": 410,
      "dots": 535.53,
      "equipment": "Wraps",
      "sex": "M",
      "weight_class_kg": "145+",
      "bodyweight_kg": 184.07
    }
  },
  {
    "id": "first-f-raw-squat-200",
    "title": "First woman to squat 200 kg raw",
    "story": "The 200 squat is roughly the female counterpart to a 300 squat for men — a strength-Dots equivalent that has its own mythology.",
    "icon": "⬇️",
    "threshold": 200,
    "threshold_field": "best3_squat_kg",
    "lifter": {
      "lifter_id": 183312,
      "name": "Jill Mills"
    },
    "meet": {
      "meet_id": 2218,
      "meet_name": "Texas No Gear",
      "federation": "APF",
      "meet_date": "1998-04-26"
    },
    "result": {
      "total": 590,
      "squat": 215,
      "bench": 142.5,
      "deadlift": 232.5,
      "dots": 547.6,
      "equipment": "Raw",
      "sex": "F",
      "weight_class_kg": "82.5",
      "bodyweight_kg": 82.5
    }
  },
  {
    "id": "first-f-raw-total-500",
    "title": "First woman to total 500 kg raw",
    "story": "The 500 club opened the door between \"nationally strong\" and \"globally elite\" for women lifting without a suit.",
    "icon": "🌟",
    "threshold": 500,
    "threshold_field": "total_kg",
    "lifter": {
      "lifter_id": 183312,
      "name": "Jill Mills"
    },
    "meet": {
      "meet_id": 2218,
      "meet_name": "Texas No Gear",
      "federation": "APF",
      "meet_date": "1998-04-26"
    },
    "result": {
      "total": 590,
      "squat": 215,
      "bench": 142.5,
      "deadlift": 232.5,
      "dots": 547.6,
      "equipment": "Raw",
      "sex": "F",
      "weight_class_kg": "82.5",
      "bodyweight_kg": 82.5
    }
  },
  {
    "id": "first-f-raw-total-600",
    "title": "First woman to total 600 kg raw",
    "story": "A modern phenomenon. The first 600 was crossed within a generation of women being widely admitted to competitive powerlifting.",
    "icon": "🌟",
    "threshold": 600,
    "threshold_field": "total_kg",
    "lifter": {
      "lifter_id": 366519,
      "name": "April Mathis"
    },
    "meet": {
      "meet_id": 2516,
      "meet_name": "Southern States",
      "federation": "APF",
      "meet_date": "2009-12-05"
    },
    "result": {
      "total": 637.5,
      "squat": 237.5,
      "bench": 165,
      "deadlift": 235,
      "dots": null,
      "equipment": "Raw",
      "sex": "F",
      "weight_class_kg": "90+",
      "bodyweight_kg": null
    }
  },
  {
    "id": "first-f-raw-bench-150",
    "title": "First woman to bench 150 kg raw",
    "story": "Raw bench is the hardest lift for women to scale. One hundred and fifty kilos with no shirt is the female analog to a man's 250 — elite for any bodyweight.",
    "icon": "💪",
    "threshold": 150,
    "threshold_field": "best3_bench_kg",
    "lifter": {
      "lifter_id": 366519,
      "name": "April Mathis"
    },
    "meet": {
      "meet_id": 2516,
      "meet_name": "Southern States",
      "federation": "APF",
      "meet_date": "2009-12-05"
    },
    "result": {
      "total": 637.5,
      "squat": 237.5,
      "bench": 165,
      "deadlift": 235,
      "dots": null,
      "equipment": "Raw",
      "sex": "F",
      "weight_class_kg": "90+",
      "bodyweight_kg": null
    }
  },
  {
    "id": "first-f-raw-deadlift-250",
    "title": "First woman to deadlift 250 kg raw",
    "story": "For most of recorded powerlifting history, a 250 deadlift was elite for ANY un-suited woman regardless of class.",
    "icon": "⬆️",
    "threshold": 250,
    "threshold_field": "best3_deadlift_kg",
    "lifter": {
      "lifter_id": 366519,
      "name": "April Mathis"
    },
    "meet": {
      "meet_id": 29051,
      "meet_name": "Raw Unity III",
      "federation": "RUPC",
      "meet_date": "2010-01-30"
    },
    "result": {
      "total": 680,
      "squat": 245,
      "bench": 182.5,
      "deadlift": 252.5,
      "dots": 548.94,
      "equipment": "Raw",
      "sex": "F",
      "weight_class_kg": "90+",
      "bodyweight_kg": 117.5
    }
  },
  {
    "id": "first-f-raw-total-700",
    "title": "First woman to total 700 kg raw",
    "story": "The 700 club opened for women a generation after Jan Todd cracked 400 — a one-hundred-kilo jump per decade once the sport welcomed serious depth.",
    "icon": "🌟",
    "threshold": 700,
    "threshold_field": "total_kg",
    "lifter": {
      "lifter_id": 366519,
      "name": "April Mathis"
    },
    "meet": {
      "meet_id": 2541,
      "meet_name": "Tampa Barbell Invitational",
      "federation": "APF",
      "meet_date": "2010-06-05"
    },
    "result": {
      "total": 705.34,
      "squat": 272.16,
      "bench": 174.63,
      "deadlift": 258.55,
      "dots": 575.65,
      "equipment": "Raw",
      "sex": "F",
      "weight_class_kg": "90+",
      "bodyweight_kg": 113.4
    }
  },
  {
    "id": "first-f-raw-squat-250",
    "title": "First woman to squat 250 kg raw",
    "story": "A quarter-tonne back squat, no suit — the number that pushed the women's raw squat into territory that would have won most open meets a generation ago.",
    "icon": "⬇️",
    "threshold": 250,
    "threshold_field": "best3_squat_kg",
    "lifter": {
      "lifter_id": 366519,
      "name": "April Mathis"
    },
    "meet": {
      "meet_id": 2541,
      "meet_name": "Tampa Barbell Invitational",
      "federation": "APF",
      "meet_date": "2010-06-05"
    },
    "result": {
      "total": 705.34,
      "squat": 272.16,
      "bench": 174.63,
      "deadlift": 258.55,
      "dots": 575.65,
      "equipment": "Raw",
      "sex": "F",
      "weight_class_kg": "90+",
      "bodyweight_kg": 113.4
    }
  },
  {
    "id": "first-f-raw-dots-600",
    "title": "First woman to score 600 Dots raw",
    "story": "Women's Dots ceilings have climbed dramatically over the past decade — the first 600 marked the moment the sport got globally competitive across weight classes.",
    "icon": "📊",
    "threshold": 600,
    "threshold_field": "dots",
    "lifter": {
      "lifter_id": 366519,
      "name": "April Mathis"
    },
    "meet": {
      "meet_id": 29626,
      "meet_name": "North Carolina Raw Pro-Am",
      "federation": "SPF",
      "meet_date": "2012-08-04"
    },
    "result": {
      "total": 748.43,
      "squat": 294.84,
      "bench": 192.78,
      "deadlift": 260.82,
      "dots": 601,
      "equipment": "Wraps",
      "sex": "F",
      "weight_class_kg": "90+",
      "bodyweight_kg": 119.66
    }
  },
  {
    "id": "first-m-raw-total-1100",
    "title": "First man to total 1100 kg raw",
    "story": "The next hundred beyond the four-digit barrier. A raw total once reserved for multi-ply gear, now posted in sleeves.",
    "icon": "👑",
    "threshold": 1100,
    "threshold_field": "total_kg",
    "lifter": {
      "lifter_id": 91687,
      "name": "Andrey Malanichev"
    },
    "meet": {
      "meet_id": 14837,
      "meet_name": "World Championships",
      "federation": "GPA",
      "meet_date": "2013-12-05"
    },
    "result": {
      "total": 1100,
      "squat": 435,
      "bench": 260,
      "deadlift": 405,
      "dots": 595.87,
      "equipment": "Wraps",
      "sex": "M",
      "weight_class_kg": "140+",
      "bodyweight_kg": 146
    }
  },
  {
    "id": "first-m-raw-dots-600",
    "title": "First man to score 600 Dots raw",
    "story": "Dots normalizes for bodyweight — so this is the first time any man, anywhere in the weight-class ladder, scored 600 in the modern coefficient without a suit.",
    "icon": "📊",
    "threshold": 600,
    "threshold_field": "dots",
    "lifter": {
      "lifter_id": 91687,
      "name": "Andrey Malanichev"
    },
    "meet": {
      "meet_id": 29055,
      "meet_name": "Raw Unity VII",
      "federation": "RUPC",
      "meet_date": "2014-02-07"
    },
    "result": {
      "total": 1120,
      "squat": 460,
      "bench": 260,
      "deadlift": 400,
      "dots": 607.39,
      "equipment": "Wraps",
      "sex": "M",
      "weight_class_kg": "140+",
      "bodyweight_kg": 145.4
    }
  },
  {
    "id": "first-f-raw-squat-300",
    "title": "First woman to squat 300 kg raw",
    "story": "The women's counterpart to a man's 400 — three hundred kilos out of the rack without a suit, the outer edge of what the sport has recorded.",
    "icon": "⬇️",
    "threshold": 300,
    "threshold_field": "best3_squat_kg",
    "lifter": {
      "lifter_id": 780810,
      "name": "Samantha DiBois"
    },
    "meet": {
      "meet_id": 44876,
      "meet_name": "Relentless Minnesota",
      "federation": "UPA",
      "meet_date": "2016-07-08"
    },
    "result": {
      "total": 707.5,
      "squat": 300,
      "bench": 177.5,
      "deadlift": 230,
      "dots": 545.31,
      "equipment": "Wraps",
      "sex": "F",
      "weight_class_kg": "90+",
      "bodyweight_kg": 163.61
    }
  },
  {
    "id": "first-f-raw-dots-650",
    "title": "First woman to score 650 Dots raw",
    "story": "Half a century after Jan Todd entered the historical record, women's coefficient ceilings cracked numbers that would have outscored most male lifters of the 70s and 80s.",
    "icon": "💎",
    "threshold": 650,
    "threshold_field": "dots",
    "lifter": {
      "lifter_id": 351690,
      "name": "Chakera Ingram"
    },
    "meet": {
      "meet_id": 52038,
      "meet_name": "Kern US Open",
      "federation": "USPA",
      "meet_date": "2018-05-12"
    },
    "result": {
      "total": 720,
      "squat": 280,
      "bench": 165,
      "deadlift": 275,
      "dots": 684.02,
      "equipment": "Wraps",
      "sex": "F",
      "weight_class_kg": "82.5",
      "bodyweight_kg": 78.7
    }
  },
  {
    "id": "first-f-raw-deadlift-275",
    "title": "First woman to deadlift 275 kg raw",
    "story": "The deadlift is the most democratic lift in powerlifting — anyone can train it anywhere. 275 raw for a woman is the modern benchmark of all-time-pull territory.",
    "icon": "⬆️",
    "threshold": 275,
    "threshold_field": "best3_deadlift_kg",
    "lifter": {
      "lifter_id": 351690,
      "name": "Chakera Ingram"
    },
    "meet": {
      "meet_id": 52038,
      "meet_name": "Kern US Open",
      "federation": "USPA",
      "meet_date": "2018-05-12"
    },
    "result": {
      "total": 720,
      "squat": 280,
      "bench": 165,
      "deadlift": 275,
      "dots": 684.02,
      "equipment": "Wraps",
      "sex": "F",
      "weight_class_kg": "82.5",
      "bodyweight_kg": 78.7
    }
  },
  {
    "id": "first-f-raw-dots-700",
    "title": "First woman to score 700 Dots raw",
    "story": "The pound-for-pound ceiling for women's raw lifting — a coefficient that outscores the vast majority of male competitors in history.",
    "icon": "💎",
    "threshold": 700,
    "threshold_field": "dots",
    "lifter": {
      "lifter_id": 120751,
      "name": "Marianna Gasparyan"
    },
    "meet": {
      "meet_id": 60691,
      "meet_name": "Kern US Open",
      "federation": "WRPF",
      "meet_date": "2019-04-27"
    },
    "result": {
      "total": 612.5,
      "squat": 260,
      "bench": 132.5,
      "deadlift": 220,
      "dots": 709.96,
      "equipment": "Wraps",
      "sex": "F",
      "weight_class_kg": "56",
      "bodyweight_kg": 56
    }
  },
  {
    "id": "first-m-raw-dots-650",
    "title": "First man to score 650 Dots raw",
    "story": "Dots normalizes for bodyweight — so a man scoring 650 in a no-suit configuration meant the sport had finally produced lifters who looked elite at every class, not just the heaviest.",
    "icon": "💎",
    "threshold": 650,
    "threshold_field": "dots",
    "lifter": {
      "lifter_id": 68342,
      "name": "Yury Belkin"
    },
    "meet": {
      "meet_id": 50977,
      "meet_name": "Boss of Bosses 6",
      "federation": "USA-UA",
      "meet_date": "2019-08-30"
    },
    "result": {
      "total": 1066,
      "squat": 416,
      "bench": 235,
      "deadlift": 415,
      "dots": 656.14,
      "equipment": "Wraps",
      "sex": "M",
      "weight_class_kg": "100",
      "bodyweight_kg": 100
    }
  },
  {
    "id": "first-m-raw-deadlift-450",
    "title": "First man to deadlift 450 kg raw",
    "story": "A number that lived only in deadlift suits and bench shirts for decades. Crossing it raw meant the equipped era no longer owned the deadlift record book.",
    "icon": "⬆️",
    "threshold": 450,
    "threshold_field": "best3_deadlift_kg",
    "lifter": {
      "lifter_id": 76135,
      "name": "Danny Grigsby"
    },
    "meet": {
      "meet_id": 53335,
      "meet_name": "Virginia Beach Classic 2",
      "federation": "USPA",
      "meet_date": "2022-03-26"
    },
    "result": {
      "total": 940,
      "squat": 275,
      "bench": 200,
      "deadlift": 465,
      "dots": 531.55,
      "equipment": "Raw",
      "sex": "M",
      "weight_class_kg": "140",
      "bodyweight_kg": 125.9
    }
  }
];
